import { clientConsoleLogger } from '@utils/logger/internal/console';
import { newRelicReport } from '@utils/logger/internal/newrelic';

interface Params {
	log_message: string;
	newRelic?: boolean;
	optionalParams?: Record<string, string | number | boolean>;
	errorToLog?: Error;
}

type ErrorParams = Params & Required<Pick<Params, 'errorToLog'>>;

const logger = (
	level: 'log' | 'warn' | 'error',
	newRelicLevel: 'INFO' | 'WARN' | 'ERROR',
	{ log_message, errorToLog, optionalParams = {}, newRelic }: Params,
) => {
	if (SERVER) {
		const serverLogger = require('@server/utils/serverLogger');

		serverLogger.ServerLogger[level](
			log_message,
			errorToLog ? { ...optionalParams, error: errorToLog } : optionalParams,
		);
	} else {
		clientConsoleLogger(level, log_message, errorToLog, optionalParams);
	}

	if (newRelic) {
		// Sanitize optionalParams: only forward primitive values to New Relic
		// to prevent complex objects (e.g. membership, response) from being sent
		const safeAttributes = Object.fromEntries(
			Object.entries(optionalParams).filter(
				([, v]) =>
					typeof v === 'string' ||
					typeof v === 'number' ||
					typeof v === 'boolean',
			),
		) as Record<string, string | number | boolean>;
		newRelicReport(newRelicLevel, log_message, errorToLog, safeAttributes);
	}
};

export const Logger = {
	log: (params: Omit<Params, 'errorToLog'>) => logger('log', 'INFO', params),
	warn: (params: Omit<Params, 'errorToLog'>) => logger('warn', 'WARN', params),
	error: (params: ErrorParams) => logger('error', 'ERROR', params),
};
